// base
import React, { useRef, useMemo } from 'react';

// libraries
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  type ChartOptions,
  type ScriptableContext
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

export interface DoubleLineChartDataSetProps {
  label: string;
  data: number[];
  borderColor: string;
  pointBackgroundColor: string;
  pointHoverBorderColor: string;
  gradientStart: string;
  gradientEnd: string;
  gradientStartPosition: number;
  gradientEndPosition: number;
  tension?: number;
}

interface DoubleLineChartProps {
  labels: string[];
  customOptions?: ChartOptions<'line'>;
  dataSets: DoubleLineChartDataSetProps[];
}

export const DoubleLineChart: React.FC<DoubleLineChartProps> = ({
  labels,
  dataSets,
  customOptions
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
            color: '#2222224D'
          },
          ticks: {
            color: '#222222'
          }
        },
        y: {
          grid: {
            display: true,
            color: '#2222224D',
            borderDash: [6, 4],
            lineWidth: 1
          },
          ticks: {
            color: '#FFFFFF',
            stepSize: 5
          },
          border: {
            dash: [2, 4]
          },
          min: 0
          // max: 1000
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: '#ffffff',
          borderWidth: 1
        }
      },
      ...customOptions
    }),
    [customOptions, labels]
  );

  const chartData = useMemo(
    () => ({
      labels: labels,
      datasets: dataSets.map((dataset) => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: (context: ScriptableContext<'line'>) => {
          const ctx = context.chart.ctx;
          const chartArea = context.chart.chartArea;
          if (!chartArea) {
            return undefined;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);

          gradient.addColorStop(dataset.gradientStartPosition, dataset.gradientStart);
          gradient.addColorStop(dataset.gradientEndPosition, dataset.gradientEnd);

          return gradient;
        },
        borderColor: dataset.borderColor,
        borderWidth: 2,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: dataset.borderColor,
        pointBorderWidth: 2,
        pointHoverBackgroundColor: dataset.borderColor,
        pointHoverBorderColor: dataset.borderColor,
        pointRadius: 3,
        pointHoverRadius: 5,
        fill: true,
        tension: dataset.tension ?? 0.4
      }))
    }),
    [labels, dataSets]
  );

  return <Line ref={chartRef} data={chartData} options={options} updateMode="resize" />;
};
